/**
 * Turns whatever an admin saved in a branch's "Map URL" field into something an
 * <iframe> can actually load.
 *
 * The field is free text, so it realistically contains any of:
 *   - nothing at all                     -> build from lat/long, else the address
 *   - a full <iframe ...> snippet copied from Google's Share > Embed dialog
 *   - an /maps/embed/v1 or output=embed URL (already embeddable)
 *   - a normal maps link carrying coordinates (?q=, ?query=, or @lat,lng)
 *   - a shortened maps.app.goo.gl link, which cannot be resolved in the browser
 *
 * Anything we can't turn into an embed falls back to coordinates, then to a
 * plain address search, so the map is never blank when we have any location data.
 */

const EMBED_BASE = "https://maps.google.com/maps";

const extractIframeSrc = (value) => {
  const match = value.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

/** DECIMAL columns arrive as strings, and Number(null) is 0 — guard both. */
const coordsFrom = (branch) => {
  const { latitude, longitude } = branch || {};
  if (latitude === null || latitude === undefined || latitude === "") return null;
  if (longitude === null || longitude === undefined || longitude === "") return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;

  return { lat, lng };
};

const addressQuery = (branch) =>
  [
    branch?.address,
    branch?.city,
    branch?.state,
    branch?.postalCode,
    branch?.country,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .join(", ");

const COORD_PATTERNS = [
  /[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
  /[?&]query=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
  /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
];

/** Embeddable URL for the <iframe>, or null when there's nothing to show. */
export function toMapEmbedUrl(branch) {
  const raw = (branch?.hasMap || "").trim();

  if (raw) {
    const src = raw.startsWith("<") ? extractIframeSrc(raw) : raw;

    if (src) {
      if (/\/maps\/embed/i.test(src) || /[?&]output=embed/i.test(src)) {
        return src;
      }
      for (const pattern of COORD_PATTERNS) {
        const match = src.match(pattern);
        if (match) {
          return `${EMBED_BASE}?q=${match[1]},${match[2]}&z=16&output=embed`;
        }
      }
      // A share/short link with no coordinates in it — can't be expanded
      // client-side, so fall through to the branch's own data.
    }
  }

  const coords = coordsFrom(branch);
  if (coords) {
    return `${EMBED_BASE}?q=${coords.lat},${coords.lng}&z=16&output=embed`;
  }

  const query = addressQuery(branch);
  return query
    ? `${EMBED_BASE}?q=${encodeURIComponent(query)}&z=15&output=embed`
    : null;
}

/** Full Google Maps link for the "Get directions" button. */
export function toDirectionsUrl(branch) {
  const raw = (branch?.hasMap || "").trim();

  // A real maps link the admin curated beats anything we can synthesise
  if (raw && !raw.startsWith("<") && /^https?:\/\//i.test(raw)) {
    if (!/\/maps\/embed/i.test(raw) && !/[?&]output=embed/i.test(raw)) {
      return raw;
    }
  }

  const coords = coordsFrom(branch);
  if (coords) {
    return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
  }

  const query = addressQuery(branch);
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : null;
}

export function hasMappableLocation(branch) {
  return toMapEmbedUrl(branch) !== null;
}
